/**
 * Les retours fournisseur (V1.6-10A).
 *
 * Cet écran répond à « qu'est-ce qu'on renvoie, et est-ce parti ». Il ne
 * prépare pas de retour lui-même : un retour naît sur la fiche d'une commande,
 * là où l'on sait ce qui a été reçu et ce qu'il en reste de renvoyable.
 *
 * **Un brouillon n'a rien fait sortir.** La distinction est la même qu'entre
 * compter sur un quai et faire entrer en stock : tant qu'un retour est un
 * brouillon, la marchandise est encore sur la tablette. La colonne « Statut »
 * est donc la première chose que l'écran doit rendre lisible.
 */
import { Link, useSearchParams } from "react-router-dom";
import { Undo2 } from "lucide-react";

import { useSupplierReturns } from "@/api/supplier-returns";
import type { SupplierReturn, SupplierReturnStatus } from "@/api/types";
import { Pagination, TableShell, Td, Th, Tr } from "@/components/DataTable";
import { EmptyState, ErrorState, TableSkeleton } from "@/components/States";
import { StatusBadge } from "@/components/StatusBadge";
import { Page } from "@/layout/Page";
import { PageHeader } from "@/layout/PageHeader";
import { formatDateTime, formatSupplierReturnStatus, pluralize } from "@/lib/format";
import { supplierReturnTone } from "@/lib/stock";

const PAGE_SIZE = 50;

const FILTERS: { value: "" | SupplierReturnStatus; label: string }[] = [
  { value: "", label: "Tous" },
  { value: "DRAFT", label: "Brouillons" },
  { value: "FINALIZED", label: "Envoyés" },
];

export function SupplierReturnsPage() {
  const [params, setParams] = useSearchParams();
  const offset = Number(params.get("offset") ?? 0);
  const status = (params.get("status") ?? "") as "" | SupplierReturnStatus;

  const { data, isLoading, error, refetch } = useSupplierReturns({
    limit: PAGE_SIZE,
    offset,
    ...(status ? { status } : {}),
  });

  function goTo(next: number) {
    const merged = new URLSearchParams(params);
    if (next === 0) merged.delete("offset");
    else merged.set("offset", String(next));
    setParams(merged, { replace: true });
  }

  function filter(next: "" | SupplierReturnStatus) {
    const merged = new URLSearchParams(params);
    merged.delete("offset");
    if (next) merged.set("status", next);
    else merged.delete("status");
    setParams(merged, { replace: true });
  }

  return (
    <Page>
      <PageHeader
        title="Retours fournisseur"
        subtitle="De la marchandise reçue qui repart. Un brouillon ne fait rien sortir ; finaliser écrit le mouvement."
      />

      <div className="mb-3 flex flex-wrap gap-1.5">
        {FILTERS.map((item) => (
          <button
            key={item.value || "all"}
            type="button"
            onClick={() => filter(item.value)}
            className={`rounded-md border px-2.5 py-1 text-[12px] ${
              status === item.value
                ? "border-brand/40 bg-brand/10 text-brand"
                : "border-border text-ink-muted hover:text-ink"
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>

      <div className="flex min-w-0 flex-col overflow-hidden rounded-lg border border-border bg-surface">
        <div className="min-w-0">
          {isLoading && <TableSkeleton rows={8} columns={5} />}
          {error && <ErrorState error={error} onRetry={() => void refetch()} />}

          {data && data.items.length === 0 && (
            <EmptyState
              icon={<Undo2 className="size-6" aria-hidden />}
              title="Aucun retour"
              description="Un retour se prépare depuis la fiche d'une commande, sur ce qui a réellement été reçu."
            />
          )}

          {data && data.items.length > 0 && (
            <>
              <div className="hidden md:block">
                <TableShell>
                  <thead>
                    <tr>
                      <Th className="w-32 whitespace-nowrap">Retour</Th>
                      <Th className="max-w-0">Fournisseur</Th>
                      <Th>Statut</Th>
                      <Th align="right" className="whitespace-nowrap">
                        Lignes
                      </Th>
                      <Th className="hidden whitespace-nowrap @3xl:table-cell">Autorisation</Th>
                      <Th className="hidden whitespace-nowrap @4xl:table-cell">Envoyé le</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((item) => (
                      <ReturnRow key={item.id} item={item} />
                    ))}
                  </tbody>
                </TableShell>
              </div>

              <ul className="divide-y divide-border/60 md:hidden">
                {data.items.map((item) => (
                  <li key={item.id}>
                    <ReturnCard item={item} />
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        {data && data.total > PAGE_SIZE && (
          <Pagination offset={offset} limit={PAGE_SIZE} total={data.total} onChange={goTo} />
        )}
      </div>
    </Page>
  );
}

function ReturnRow({ item }: { item: SupplierReturn }) {
  return (
    <Tr>
      <Td className="whitespace-nowrap font-mono text-[12px] text-ink-muted">
        <Link to={`/retours-fournisseur/${item.id}`} className="hover:text-brand">
          {item.code}
        </Link>
      </Td>
      <Td className="max-w-0">
        <Link
          to={`/retours-fournisseur/${item.id}`}
          className="block truncate font-medium text-ink hover:text-brand"
        >
          {item.supplier_name}
        </Link>
      </Td>
      <Td>
        <StatusBadge tone={supplierReturnTone(item.status)}>
          {formatSupplierReturnStatus(item.status)}
        </StatusBadge>
      </Td>
      <Td align="right" className="tnum text-[13px] text-ink-muted">
        {item.line_count}
      </Td>
      <Td className="hidden whitespace-nowrap font-mono text-[12px] text-ink-subtle @3xl:table-cell">
        {item.rma_reference ?? "—"}
      </Td>
      <Td className="hidden whitespace-nowrap text-[12px] text-ink-subtle @4xl:table-cell">
        {item.finalized_at ? formatDateTime(item.finalized_at) : "—"}
      </Td>
    </Tr>
  );
}

/** Le même retour, pour un pouce sur un iPhone. */
function ReturnCard({ item }: { item: SupplierReturn }) {
  return (
    <Link to={`/retours-fournisseur/${item.id}`} className="block px-3 py-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-[14px] font-medium text-ink">{item.supplier_name}</p>
          <p className="font-mono text-[11px] text-ink-subtle">{item.code}</p>
        </div>
        <StatusBadge tone={supplierReturnTone(item.status)}>
          {formatSupplierReturnStatus(item.status)}
        </StatusBadge>
      </div>
      <p className="mt-1 text-[12px] text-ink-subtle">
        {pluralize(item.line_count, "article", "articles")}
        {item.rma_reference ? ` · autorisation ${item.rma_reference}` : ""}
      </p>
    </Link>
  );
}
